import { Button, Modal, Input, InputNumber, message } from "antd";
import { Box, MapPin, Package, Zap } from "lucide-react";
import { FC, useState } from "react";
import { useCreateInventory, useUpdateInventory } from "~/services/inventory";
import { useGetLocationsInfinite } from "~/services/common";
import { InfiniteScrollSelect } from "~/components/common";
import { TInventory } from "~/types";
import { capitalize, formatDimensions } from "~/utils";
import { ContainerType, InventoryType, InventoryStatusCode } from "~/utils/constants";

type ContainerDefaults = { length: number; width: number; height: number; weight: number };

const containerTypeDefaults: Record<ContainerType, ContainerDefaults> = {
  [ContainerType.CBIN]:      { length: 48, width: 28, height: 24, weight: 50 },
  [ContainerType.CRATE]:     { length: 48, width: 36, height: 36, weight: 40 },
  [ContainerType.SLATCRATE]: { length: 48, width: 36, height: 36, weight: 30 },
  [ContainerType.T_Frame]:   { length: 72, width: 6,  height: 48, weight: 35 },
  [ContainerType.BOX]:       { length: 24, width: 18, height: 18, weight: 10 },
  [ContainerType.PALLET]:    { length: 48, width: 40, height: 6,  weight: 45 },
  [ContainerType.SLIPCASE]:  { length: 36, width: 4,  height: 48, weight: 15 },
  [ContainerType.CUSTOM]:    { length: 0,  width: 0,  height: 0,  weight: 0 },
  [ContainerType.PACKAGE]:   { length: 0,  width: 0,  height: 0,  weight: 0 },
  [ContainerType.PIECE]:     { length: 0,  width: 0,  height: 0,  weight: 0 },
};

const containerTypeOptions = [
  { label: "C-Bin", value: ContainerType.CBIN },
  { label: "Crate", value: ContainerType.CRATE },
  { label: "Slat Crate", value: ContainerType.SLATCRATE },
  { label: "T-Frame", value: ContainerType.T_Frame },
  { label: "Box", value: ContainerType.BOX },
  { label: "Pallet", value: ContainerType.PALLET },
  { label: "Slipcase", value: ContainerType.SLIPCASE },
  { label: "Custom", value: ContainerType.CUSTOM },
];

type Props = {
  open: boolean;
  onClose: () => void;
  selectedPieces: TInventory[];
  onSuccess?: () => void;
};

export const QuickConsolidateModal: FC<Props> = ({
  open,
  onClose,
  selectedPieces,
  onSuccess,
}) => {
  const [containerType, setContainerType] = useState<ContainerType>(ContainerType.CBIN);
  const [title, setTitle] = useState("");
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const [dimensions, setDimensions] = useState<ContainerDefaults>(
    containerTypeDefaults[ContainerType.CBIN]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createInventoryMutation = useCreateInventory();
  const updateInventoryMutation = useUpdateInventory();
  const locationsQuery = useGetLocationsInfinite();

  // All pieces must be from the same client
  const clientId = selectedPieces[0]?.clientId;
  const clientName =
    selectedPieces[0]?.client?.companyName || "Unknown Client";
  const commonStatus = selectedPieces[0]?.status;

  // Auto-fill dimensions when container type changes
  const handleContainerTypeChange = (type: ContainerType) => {
    setContainerType(type);
    setDimensions(containerTypeDefaults[type]);
  };

  const handleQuickConsolidate = async () => {
    if (selectedPieces.length === 0) return;

    setIsSubmitting(true);
    try {
      // Step 1: Create the package
      const packageTitle =
        title.trim() ||
        `${capitalize(containerType)} - ${clientName} (${selectedPieces.length} pieces)`;

      const result = await createInventoryMutation.mutateAsync({
        title: packageTitle,
        clientId,
        itemType: InventoryType.PACKAGE,
        containerType,
        status: commonStatus || InventoryStatusCode.AVAILABLE_ON_HAND,
        locationId: locationId || undefined,
        length: dimensions.length,
        width: dimensions.width,
        height: dimensions.height,
        weight: dimensions.weight,
        specialHandling: [],
        duplicates: 0,
      } as any);

      // Backend returns an array of created inventories
      const newPackage = Array.isArray(result) ? result[0] : result;
      if (!newPackage?.id) {
        throw new Error("Failed to create package — no ID returned");
      }

      // Step 2: Consolidate pieces into the new package
      const pieceIds = selectedPieces.map((p) => p.id);
      await updateInventoryMutation.mutateAsync({
        id: newPackage.id,
        pieceIds,
      });

      // Step 3: Update each piece's location to match the container
      if (locationId) {
        for (const piece of selectedPieces) {
          if (piece.locationId !== locationId) {
            await updateInventoryMutation.mutateAsync({
              id: piece.id,
              locationId,
            });
          }
        }
      }

      message.success(
        `Created ${capitalize(containerType)} and consolidated ${selectedPieces.length} piece(s)`
      );
      handleClose();
      onSuccess?.();
    } catch (err) {
      console.error("Quick consolidate error:", err);
      message.error("Failed to quick consolidate. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setContainerType(ContainerType.CBIN);
    setTitle("");
    setLocationId(undefined);
    setDimensions(containerTypeDefaults[ContainerType.CBIN]);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      width={700}
      footer={null}
      title={
        <div className="flex items-center space-x-2">
          <Zap size={20} className="text-amber-500" />
          <span>Quick Consolidate</span>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Client & status info */}
        <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
          <div className="text-sm font-medium text-blue-800">
            Client: {clientName}
          </div>
          <div className="text-sm text-blue-600">
            {selectedPieces.length} piece(s) will be consolidated into a new
            package
          </div>
          <div className="text-sm text-blue-600">
            Status: {capitalize(commonStatus)}
          </div>
        </div>

        {/* Container Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Package Type
          </label>
          <div className="grid grid-cols-4 gap-2">
            {containerTypeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleContainerTypeChange(option.value)}
                className={`p-3 border rounded-lg text-center text-sm font-medium transition-colors ${
                  containerType === option.value
                    ? "bg-blue-50 border-blue-500 text-blue-700"
                    : "hover:bg-gray-50 border-gray-200 text-gray-700"
                }`}
              >
                <Box
                  size={20}
                  className={`mx-auto mb-1 ${
                    containerType === option.value
                      ? "text-blue-500"
                      : "text-gray-400"
                  }`}
                />
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dimensions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dimensions (inches) & Weight (lbs)
          </label>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Length</label>
              <InputNumber
                min={0}
                value={dimensions.length}
                onChange={(v) => setDimensions((d) => ({ ...d, length: v || 0 }))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Width</label>
              <InputNumber
                min={0}
                value={dimensions.width}
                onChange={(v) => setDimensions((d) => ({ ...d, width: v || 0 }))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Height</label>
              <InputNumber
                min={0}
                value={dimensions.height}
                onChange={(v) => setDimensions((d) => ({ ...d, height: v || 0 }))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Weight</label>
              <InputNumber
                min={0}
                value={dimensions.weight}
                onChange={(v) => setDimensions((d) => ({ ...d, weight: v || 0 }))}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Title and Location row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Package Title (optional)
            </label>
            <Input
              placeholder={`e.g., ${capitalize(containerType)} - ${clientName}`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave blank for auto-generated title
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center space-x-1">
                <MapPin size={14} />
                <span>Location</span>
              </span>
            </label>
            <InfiniteScrollSelect
              placeholder="Select location"
              queryResult={locationsQuery}
              getOptionLabel={(location) => location.name}
              getOptionValue={(location) => location.id}
              value={locationId}
              onChange={(value) => setLocationId(value || undefined)}
              allOptions={{ value: "", label: "No location" }}
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Sets location for the package and all pieces
            </p>
          </div>
        </div>

        {/* Pieces list */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pieces to Consolidate ({selectedPieces.length})
          </label>
          <div className="border rounded-lg max-h-48 overflow-y-auto divide-y">
            {selectedPieces.map((piece) => (
              <div
                key={piece.id}
                className="px-3 py-2 flex items-center justify-between"
              >
                <div className="flex items-center space-x-2">
                  <Package size={14} className="text-green-500" />
                  <div>
                    <div className="text-sm font-medium">{piece.title}</div>
                    <div className="text-xs text-gray-500">
                      {piece.inventoryRef} • {piece.artistName}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {formatDimensions(piece)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-2 border-t">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleQuickConsolidate}
            loading={isSubmitting}
            disabled={selectedPieces.length === 0}
            icon={<Zap size={14} />}
          >
            Create & Consolidate
          </Button>
        </div>
      </div>
    </Modal>
  );
};
